import { useCallback, useEffect, useRef, useState } from 'react';
import { isValidExtension } from '../utils/media';

/**
 * Per-file metadata posted to POST /api/files and later handed to the Sheet.
 * Field names/shape mirror what react-dropzone-uploader produced so the server
 * contract and the Sheet's `metadata.name`/`metadata.type` reads keep working.
 */
export interface UploadMeta {
  name: string;
  type: string; // MIME type (e.g. "audio/mpeg")
  size: number;
  lastModifiedDate: Date;
  duration: number | null;
}

export type UploadStatus =
  | 'staged'
  | 'error_validation'
  | 'getting_params'
  | 'uploading'
  | 'done'
  | 'error'
  | 'aborted';

export interface UploadItem {
  id: string;
  file: File;
  meta: UploadMeta;
  status: UploadStatus;
  percent: number;
  errorMessage?: string;
  xhr?: XMLHttpRequest;
}

/** Shape returned by the injected getUploadParams — matches the old contract. */
export interface UploadParams {
  url: string;
  method?: string;
  body?: any;
  fields?: Record<string, string>;
  headers?: Record<string, string>;
}

export type GetUploadParams = (args: {
  file: File;
  meta: UploadMeta;
}) => Promise<UploadParams>;

export interface UseUploaderOptions {
  getUploadParams: GetUploadParams;
  onAllDone?: (metas: UploadMeta[]) => void;
  maxFiles?: number;
  maxSizeBytes?: number;
}

let itemCounter = 0;
const nextId = () => `upload-item-${++itemCounter}`;

const DURATION_TIMEOUT_MS = 10_000;

/**
 * Compute audio duration (seconds) via a throwaway <audio> element.
 * Resolves null for non-audio files, failures, or environments without Audio
 * (jsdom) so callers never block on it. A bounded timeout guarantees the
 * promise always settles (and the object URL is always revoked) even if the
 * metadata load stalls.
 */
const computeDuration = (file: File): Promise<number | null> =>
  new Promise((resolve) => {
    if (
      !file.type.startsWith('audio/') ||
      typeof Audio === 'undefined' ||
      typeof URL === 'undefined' ||
      !URL.createObjectURL
    ) {
      resolve(null);
      return;
    }
    let objectUrl: string;
    try {
      objectUrl = URL.createObjectURL(file);
    } catch {
      resolve(null);
      return;
    }
    let settled = false;
    const audio = new Audio();
    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), DURATION_TIMEOUT_MS);
    audio.addEventListener('loadedmetadata', () =>
      finish(Number.isFinite(audio.duration) ? audio.duration : null),
    );
    audio.addEventListener('error', () => finish(null));
    audio.addEventListener('abort', () => finish(null));
    audio.src = objectUrl;
  });

const buildMeta = (file: File): UploadMeta => ({
  name: file.name,
  type: file.type,
  size: file.size,
  lastModifiedDate: new Date(file.lastModified),
  duration: null,
});

/**
 * Owns the staged/uploading file list and replicates the exact upload request
 * semantics react-dropzone-uploader used (raw XHR, X-Requested-With header,
 * fields → FormData else raw body, upload.onprogress). Dependency-free and
 * unit-testable: the caller injects getUploadParams.
 */
export const useUploader = ({
  getUploadParams,
  onAllDone,
  maxFiles = 1,
  maxSizeBytes,
}: UseUploaderOptions) => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const itemsRef = useRef<UploadItem[]>(items);
  itemsRef.current = items;

  const onAllDoneRef = useRef(onAllDone);
  onAllDoneRef.current = onAllDone;
  const doneFiredRef = useRef(false);

  // Ids the user cancelled/removed. Tracked synchronously (state updates lag),
  // so an abort()-triggered readystatechange isn't misread as an upload error.
  const abortedIdsRef = useRef<Set<string>>(new Set());
  // Per-item audio-duration promise, awaited before POSTing (fixes the race
  // where the presign fired before loadedmetadata resolved).
  const durationPromisesRef = useRef<Record<string, Promise<number | null>>>({});

  const patch = useCallback((id: string, changes: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const addFiles = useCallback(
    (files: File[]): { rejected: number } => {
      const current = itemsRef.current;
      const remainingSlots = Math.max(0, maxFiles - current.length);
      const incoming = files.slice(0, remainingSlots);
      const rejected = files.length - incoming.length;

      const newItems: UploadItem[] = incoming.map((file) => {
        let status: UploadStatus = 'staged';
        let errorMessage: string | undefined;
        if (!isValidExtension(file.name)) {
          status = 'error_validation';
          errorMessage = 'Unsupported file type';
        } else if (maxSizeBytes && file.size > maxSizeBytes) {
          status = 'error_validation';
          errorMessage = `File exceeds the ${Math.round(maxSizeBytes / 1_000_000)} MB limit`;
        }
        return { id: nextId(), file, meta: buildMeta(file), status, percent: 0, errorMessage };
      });

      if (newItems.length > 0) {
        doneFiredRef.current = false;
        setItems((prev) => [...prev, ...newItems]);
        // Kick off duration computation now; store the promise so performUpload
        // can await it, and patch the resolved value back into state for display.
        newItems.forEach((it) => {
          if (it.status !== 'error_validation') {
            const promise = computeDuration(it.file);
            durationPromisesRef.current[it.id] = promise;
            promise.then((duration) => {
              if (duration !== null) {
                setItems((prev) =>
                  prev.map((cur) =>
                    cur.id === it.id ? { ...cur, meta: { ...cur.meta, duration } } : cur,
                  ),
                );
              }
            });
          }
        });
      }
      return { rejected };
    },
    [maxFiles, maxSizeBytes],
  );

  const removeFile = useCallback((id: string) => {
    // Side effects first; the setItems updater must stay pure (StrictMode
    // double-invokes it).
    const target = itemsRef.current.find((it) => it.id === id);
    abortedIdsRef.current.add(id);
    if (target?.xhr && target.status === 'uploading') target.xhr.abort();
    delete durationPromisesRef.current[id];
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    itemsRef.current.forEach((it) => {
      abortedIdsRef.current.add(it.id);
      if (it.xhr && it.status === 'uploading') it.xhr.abort();
    });
    durationPromisesRef.current = {};
    setItems([]);
    doneFiredRef.current = false;
  }, []);

  const performUpload = useCallback(
    (item: UploadItem) => {
      abortedIdsRef.current.delete(item.id);
      patch(item.id, { status: 'getting_params', percent: 0, errorMessage: undefined });

      return new Promise<void>((resolve) => {
        (async () => {
          // Ensure /api/files always receives the real audio duration.
          let meta = item.meta;
          if (meta.type.startsWith('audio/') && meta.duration == null) {
            const durationPromise = durationPromisesRef.current[item.id];
            if (durationPromise) {
              const duration = await durationPromise;
              if (duration != null) meta = { ...meta, duration };
            }
          }

          let params: UploadParams | null = null;
          try {
            params = await getUploadParams({ file: item.file, meta });
          } catch (e) {
            const cur = itemsRef.current.find((it) => it.id === item.id);
            if (!cur || abortedIdsRef.current.has(item.id)) {
              resolve();
              return;
            }
            patch(item.id, {
              status: 'error',
              errorMessage: typeof e === 'string' ? e : 'Could not start upload',
            });
            resolve();
            return;
          }

          // Cancelled or removed while the presign was in flight → drop silently.
          const current = itemsRef.current.find((it) => it.id === item.id);
          if (!current || abortedIdsRef.current.has(item.id)) {
            resolve();
            return;
          }

          if (!params || !params.url) {
            patch(item.id, { status: 'error', errorMessage: 'Could not start upload' });
            resolve();
            return;
          }

          const { url, method = 'POST', body, fields, headers = {} } = params;
          const xhr = new XMLHttpRequest();
          xhr.open(method, url, true);

          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          for (const header of Object.keys(headers)) xhr.setRequestHeader(header, headers[header]);

          // Raw body (our presigned-PUT case) sends the File directly; only
          // build a multipart FormData when there is no explicit body.
          let payload: any = body;
          if (!payload) {
            const formData = new FormData();
            if (fields) for (const field of Object.keys(fields)) formData.append(field, fields[field]);
            formData.append('file', item.file);
            payload = formData;
          }

          xhr.upload.addEventListener('progress', (e) => {
            const percent = e.total ? (e.loaded * 100.0) / e.total : 100;
            patch(item.id, { percent });
          });

          xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState !== 2 && xhr.readyState !== 4) return;

            if (xhr.status === 0) {
              // status 0 is the abort() signal; only surface it as an error when
              // the user did not initiate the cancel/remove.
              if (!abortedIdsRef.current.has(item.id)) {
                patch(item.id, { status: 'error', errorMessage: 'Upload failed' });
              }
            } else if (xhr.status > 0 && xhr.status < 400) {
              if (xhr.readyState === 4) patch(item.id, { status: 'done', percent: 100 });
            } else if (xhr.status >= 400) {
              patch(item.id, { status: 'error', errorMessage: 'Upload failed' });
            }

            if (xhr.readyState === 4) resolve();
          });

          patch(item.id, { status: 'uploading', xhr });
          xhr.send(payload);
        })();
      });
    },
    [getUploadParams, patch],
  );

  const startUpload = useCallback(() => {
    doneFiredRef.current = false;
    const uploadable = itemsRef.current.filter(
      (it) => it.status === 'staged' || it.status === 'error' || it.status === 'aborted',
    );
    uploadable.forEach((it) => performUpload(it));
  }, [performUpload]);

  const cancel = useCallback(
    (id: string) => {
      // Mark aborted (synchronously, via the ref) BEFORE abort() so the
      // readystatechange handler treats the item as a user cancel, not a failure.
      abortedIdsRef.current.add(id);
      patch(id, { status: 'aborted', percent: 0 });
      const target = itemsRef.current.find((it) => it.id === id);
      if (target?.xhr) target.xhr.abort();
    },
    [patch],
  );

  const retry = useCallback(
    (id: string) => {
      doneFiredRef.current = false;
      const target = itemsRef.current.find((it) => it.id === id);
      if (target) performUpload(target);
    },
    [performUpload],
  );

  // Fire onAllDone once per batch when the batch has settled with ≥1 success and
  // no retryable failures. error_validation rows (never uploadable) don't block;
  // 'error'/'aborted' rows keep the modal open so the user can retry.
  useEffect(() => {
    if (doneFiredRef.current || items.length === 0) return;
    const active = items.some(
      (it) =>
        it.status === 'staged' || it.status === 'getting_params' || it.status === 'uploading',
    );
    if (active) return;
    const hasRetryable = items.some((it) => it.status === 'error' || it.status === 'aborted');
    if (hasRetryable) return;
    const done = items.filter((it) => it.status === 'done');
    if (done.length === 0) return;
    doneFiredRef.current = true;
    onAllDoneRef.current?.(done.map((it) => it.meta));
  }, [items]);

  const hasStaged = items.some(
    (it) => it.status === 'staged' || it.status === 'error' || it.status === 'aborted',
  );
  const isUploading = items.some(
    (it) => it.status === 'uploading' || it.status === 'getting_params',
  );

  return {
    items,
    addFiles,
    removeFile,
    clearAll,
    startUpload,
    cancel,
    retry,
    hasStaged,
    isUploading,
  };
};

export default useUploader;
